/** Body-scale steps a form field's hint and answer can use. */
export type FieldTextSize = 'b3' | 'b4' | 'b5' | 'l1';

/**
 * Subset of the Text color variants a form field's hint and name can use.
 * `white` would vanish on the light field surface, and `error` belongs to the
 * invalid state. Kept as a literal union so Storybook reads it as an enum.
 */
export type FieldTextColor =
  | 'default'
  | 'heading'
  | 'subtext'
  | 'primary'
  | 'secondary'
  | 'accent';

export const FIELD_TEXT_SIZES: readonly FieldTextSize[] = [
  'b3',
  'b4',
  'b5',
  'l1',
];

export const FIELD_TEXT_COLORS: readonly FieldTextColor[] = [
  'default',
  'heading',
  'subtext',
  'primary',
  'secondary',
  'accent',
];

/** Text-size props shared by Input, Textarea, Select, and MultiSelect. */
export interface FieldTextStyleProps {
  /** Size of the hint shown when the field is empty. Unset keeps the field's own size. */
  placeholderSize?: FieldTextSize;
  /** Size of what the visitor types or picks. Unset keeps the field's own size. */
  valueSize?: FieldTextSize;
  /** Color of the hint shown when the field is empty. Unset uses the subtext color. */
  placeholderColor?: FieldTextColor;
  /** Color of the field name. Unset uses the body color; an invalid field stays red. */
  labelColor?: FieldTextColor;
}

export const fieldTextSizeClass: Record<FieldTextSize, string> = {
  b3: 'text-b3',
  b4: 'text-b4',
  b5: 'text-b5',
  l1: 'text-l1',
};

export const fieldPlaceholderSizeClass: Record<FieldTextSize, string> = {
  b3: 'placeholder:text-b3',
  b4: 'placeholder:text-b4',
  b5: 'placeholder:text-b5',
  l1: 'placeholder:text-l1',
};

export const fieldTextColorClass: Record<FieldTextColor, string> = {
  default: 'text-body',
  heading: 'text-heading',
  subtext: 'text-subtext',
  primary: 'text-primary-700',
  secondary: 'text-secondary-700',
  accent: 'text-text-accent',
};

export const fieldPlaceholderColorClass: Record<FieldTextColor, string> = {
  default: 'placeholder:text-body',
  heading: 'placeholder:text-heading',
  subtext: 'placeholder:text-subtext',
  primary: 'placeholder:text-primary-700',
  secondary: 'placeholder:text-secondary-700',
  accent: 'placeholder:text-text-accent',
};

/**
 * Classes for a field that shows a hint until something is picked: the hint
 * options while empty, `valueSize` once picked. An undefined `size` or
 * `emptyColor` keeps the field's own class; `label` stays red when invalid.
 */
export const pickedFieldTextClasses = (
  hasValue: boolean,
  isInvalid: boolean,
  {
    placeholderSize,
    valueSize,
    placeholderColor,
    labelColor,
  }: FieldTextStyleProps,
) => {
  const size = hasValue ? valueSize : placeholderSize;
  return {
    size: size ? fieldTextSizeClass[size] : undefined,
    emptyColor:
      !hasValue && placeholderColor
        ? fieldTextColorClass[placeholderColor]
        : undefined,
    label: isInvalid
      ? 'text-error-500'
      : labelColor
        ? fieldTextColorClass[labelColor]
        : 'text-body',
  };
};
